import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import serviceImg from "../assets/service.jpg";
import trustImg from "../assets/trust.jpg";

const fadeIn = {
  hidden: { opacity: 0, y: 40 },
  visible: { opacity: 1, y: 0 },
};

const WhyUs = () => {
  const { t } = useTranslation();

  return (
    <section className="text-white bg-black overflow-hidden">
      {/* Intro Section */}
      <motion.div
        className="py-20 px-6 md:px-12 lg:px-24 text-center bg-gradient-to-b from-black via-zinc-900 to-black"
        variants={fadeIn}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        transition={{ duration: 0.6 }}
      >
        <h2 className="text-4xl md:text-5xl font-bold mb-6 relative inline-block">
          {t("whyus.heading")}
          <span className="block h-1 w-24 bg-gradient-to-r from-sky-400 to-blue-500 mt-3 mx-auto rounded-full shadow-[0_0_15px_#38bdf8]" />
        </h2>
        <p className="text-gray-300 max-w-2xl mx-auto text-lg leading-relaxed">
          {t("whyus.subheading")}
        </p>
      </motion.div>

      {/* Row 1 */}
      <motion.div
        className="py-20 px-6 md:px-12 lg:px-24 bg-black/90 border-t border-zinc-800"
        variants={fadeIn}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        transition={{ duration: 0.6, delay: 0.2 }}
      >
        <div className="max-w-7xl mx-auto grid md:grid-cols-2 gap-14 items-center">
          {/* Text */}
          <div>
            <h3 className="text-3xl font-semibold mb-5 bg-gradient-to-r from-sky-400 to-blue-500 bg-clip-text text-transparent">
              {t("whyus.section1.title")}
            </h3>
            <p className="text-gray-300 mb-6 text-lg leading-relaxed">
              {t("whyus.section1.desc")}
            </p>
            <Link
              to="/contact"
              className="inline-block bg-gradient-to-r from-sky-500 to-blue-600 hover:from-blue-600 hover:to-sky-500 
                         transition-all duration-300 transform hover:scale-105 shadow-[0_0_15px_#38bdf8]/50 
                         px-8 py-3 rounded-xl text-white font-medium tracking-wide"
            >
              ✈️ {t("whyus.section1.button")}
            </Link>
          </div>

          {/* Image */}
          <motion.div
            className="rounded-2xl overflow-hidden shadow-[0_0_25px_#38bdf8]/20"
            whileHover={{ scale: 1.03 }}
            transition={{ type: "spring", stiffness: 120 }}
          >
            <img
              src={trustImg}
              alt={t("whyus.section1.alt")}
              className="w-full h-auto object-cover"
              loading="lazy"
            />
          </motion.div>
        </div>
      </motion.div>

      {/* Row 2 */}
      <motion.div
        className="py-20 px-6 md:px-12 lg:px-24 bg-zinc-900 border-t border-zinc-800"
        variants={fadeIn}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        transition={{ duration: 0.6, delay: 0.4 }}
      >
        <div className="max-w-7xl mx-auto grid md:grid-cols-2 gap-14 items-center">
          {/* Image */}
          <motion.div
            className="order-2 md:order-1 rounded-2xl overflow-hidden shadow-[0_0_25px_#38bdf8]/20"
            whileHover={{ scale: 1.03 }}
            transition={{ type: "spring", stiffness: 120 }}
          >
            <img
              src={serviceImg}
              alt={t("whyus.section2.alt")}
              className="w-full h-auto object-cover"
              loading="lazy"
            />
          </motion.div>

          {/* Text */}
          <div className="order-1 md:order-2">
            <h3 className="text-3xl font-semibold mb-5 bg-gradient-to-r from-blue-400 to-sky-500 bg-clip-text text-transparent">
              {t("whyus.section2.title")}
            </h3>
            <p className="text-gray-300 mb-6 text-lg leading-relaxed">
              {t("whyus.section2.desc1")}
            </p>
            <p className="text-gray-300 text-lg leading-relaxed">
              {t("whyus.section2.desc2")}
            </p>
          </div>
        </div>
      </motion.div>
    </section>
  );
};

export default WhyUs;
